import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { useGlass } from 'styles/glass';

/**
 * Glass modal used by brand creation and the per-stage edit popup in the
 * IAM table. Owns its open/close state so callers only need to pass
 * `isOpenAction` and an `onCloseAction` callback.
 *
 * The surface is not styled here — `MuiDialog` in styles/appTheme.js glasses
 * every dialog in the app. What this owns is the vertical layout: the title
 * never scrolls, and the body gets the rest of the height.
 *
 * `layout` decides who owns scrolling:
 *
 *   'scroll' (default) — the body scrolls as one block. Right for simple
 *                        forms; the caller does not have to think about it.
 *   'flex'             — the body is a full-height flex column and the child
 *                        decides what is pinned and what scrolls. Use this
 *                        when the content has its own header or footer that
 *                        has to stay put (tabs at the top, a submit button at
 *                        the bottom).
 */
export default function DialogModal(props) {
  const GLASS = useGlass();
  const {
    children, title, isOpenAction, onCloseAction, closedNotAllowed,
    layout = 'scroll', contentPadding = '20px',
  } = props;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isOpenAction) setOpen(true);
  }, [isOpenAction]);

  const handleClose = () => {
    if (closedNotAllowed) return;
    setOpen(false);
    onCloseAction && onCloseAction();
  };

  const managed = layout === 'flex';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      scroll='paper'
      PaperProps={{
        sx: {
          overflow: 'hidden',
          // Size to the content, capped at the viewport. No fixed height — that
          // left a short form stranded in a tall empty panel. The pinning still
          // works: the middle region is `flex: 1; min-height: 0; overflow: auto`,
          // which only has something to scroll once the cap is actually hit.
          maxHeight: { xs: '90vh', md: '85vh' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          // A hairline, not a filled bar — a solid header on a translucent
          // panel is the thing that made this read as a plain white modal.
          borderBottom: GLASS.divider,
          pr: '48px',
        }}
      >
        <Typography component='span' fontWeight='600'>{title}</Typography>
        {!closedNotAllowed && (
          <GlassIconButton
            label='Close'
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size='small'
          >
            <CloseIcon fontSize='small' />
          </GlassIconButton>
        )}
      </DialogTitle>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          ...(managed
            ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }
            : { overflowY: 'auto', p: contentPadding }),
        }}
      >
        {children}
      </Box>
    </Dialog>
  );
}
